// src/pages/settings/WeatherPointModal.tsx — modale "Températures & DJU" pour un site géocodé (carte des sites)
// Source unique : Météo-France (station sol la plus proche avec données complètes pour
// l'année, ou choisie manuellement) — cf. server/lib/meteoFranceDb.ts.

import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "./Modal";
import ConfirmModal from "../../components/ConfirmModal";
import { fetchNearestStations, fetchStationWeather, searchStations, type StationCandidate, type StationWeather } from "../../api/meteoFranceApi";
import type { SiteRecord } from "../../api/settingsApi";
import { MOIS_LABELS } from "../../utils/dataUtils";
import { loadHCAndRender, lerpHex } from "../../components/blocks/shared/energyChart";

const djuTooltip = (mode: DjuMode) => mode === "climatisation"
  ? "DJU base 18°C, méthode COSTIC (Tmin/Tmax) — degrés-jours de climatisation (rafraîchissement), sommés sur le mois."
  : "DJU base 18°C, méthode COSTIC (Tmin/Tmax) — degrés-jours de chauffage, sommés sur le mois.";

type Tab = "dju" | "temp";
type ViewMode = "table" | "chart";
type DjuMode = "chauffage" | "climatisation";

/** Toggle chauffage/climatisation — pilule à deux segments, visible seulement sur l'onglet DJU. */
function DjuModeToggle({ value, onChange }: { value: DjuMode; onChange: (m: DjuMode) => void }) {
  // Pastel doux — fond clair + texte de la même teinte assombri, plutôt qu'un aplat saturé + texte blanc.
  const options: [DjuMode, string, string, string][] = [
    ["chauffage",     "🔥 Chauffage",     "#f5c9d4", "#8a2f4a"],
    ["climatisation", "❄️ Climatisation", "#bfe0f0", "#1f5a7a"],
  ];
  return (
    <div style={{display:"flex",border:"1px solid #ddd",borderRadius:999,overflow:"hidden",flexShrink:0}}>
      {options.map(([id,label,bg,fg]) => (
        <button key={id} onClick={()=>onChange(id)} type="button"
          style={{padding:"4px 12px",border:"none",cursor:"pointer",fontSize:11,fontWeight:600,
            transition:"background 0.15s, color 0.15s",
            background: value===id ? bg : "transparent",
            color: value===id ? fg : "var(--text-muted)"}}>
          {label}
        </button>
      ))}
    </div>
  );
}

/** StationCandidate annotée de sa complétude sur toute la période choisie (pas seulement une année). */
interface PeriodStationCandidate extends StationCandidate {
  completeForPeriod: boolean;
  missingDaysInPeriod: number;
}

/** Libellé station : nom en couleur normale, distance/complétude en gris plus léger. */
function StationLabel({ station }: { station: PeriodStationCandidate }) {
  return (
    <>
      {station.nom}
      <span style={{color:"#aaa"}}>
        {Number.isFinite(station.distanceKm) ? ` — ${station.distanceKm} km` : ""}
        {!station.completeForPeriod ? ` — ⚠ ${station.missingDaysInPeriod} j manquants` : ""}
      </span>
    </>
  );
}

/**
 * Sélecteur de station en autocomplete — saisie libre qui filtre la liste par nom.
 * `globalSearch` (lat/lon/year) : quand fourni, une recherche serveur sur toute la base de
 * stations complète les suggestions locales dès 2 caractères tapés — utilisé quand le filtre
 * "stations complètes" est décoché, pour pouvoir rattacher n'importe quelle station au site,
 * pas seulement l'une des ~8 plus proches.
 */
function StationAutocomplete({ options, value, onChange, loading, globalSearch }: {
  options: PeriodStationCandidate[]; value: number | null; onChange: (id: number) => void; loading: boolean;
  globalSearch?: { lat: number; lon: number; year: number };
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const selected = options.find(c => c.id === value);

  const trimmed = query.trim().toLowerCase();
  const showAll = !open || trimmed === "" || trimmed === (selected?.nom ?? "").toLowerCase();

  const [searchResults, setSearchResults] = useState<PeriodStationCandidate[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!globalSearch || showAll || trimmed.length < 2) { setSearchResults([]); return; }
    let active = true;
    setSearching(true);
    const t = window.setTimeout(() => {
      searchStations(trimmed, globalSearch.lat, globalSearch.lon, globalSearch.year)
        .then(({ candidates }) => {
          if (!active) return;
          setSearchResults(candidates.map(c => ({ ...c, completeForPeriod: c.completeForYear, missingDaysInPeriod: c.missingDaysInYear })));
        })
        .catch(() => { if (active) setSearchResults([]); })
        .finally(() => { if (active) setSearching(false); });
    }, 300);
    return () => { active = false; window.clearTimeout(t); setSearching(false); };
  }, [globalSearch, showAll, trimmed]);

  const filtered = showAll ? options : [...options, ...searchResults]
    .filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i)
    .filter(c => c.nom.toLowerCase().includes(trimmed));

  return (
    <div ref={containerRef} style={{position:"relative",flex:1,minWidth:220}}>
      <input type="text" className="field"
        value={open ? query : (selected ? selected.nom : "")}
        placeholder={loading ? "Recherche…" : "Choisir une station"}
        onFocus={e => { setOpen(true); setQuery(selected ? selected.nom : ""); e.target.select(); }}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Escape") { setOpen(false); }
          if (e.key === "Enter" && filtered.length > 0) { onChange(filtered[0].id); setOpen(false); }
        }}
        style={{marginBottom:0,fontSize:12,padding:"4px 8px",width:"100%",boxSizing:"border-box"}}/>
      {open && (
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:20,
          background:"#fff",border:"1px solid #ddd",borderRadius:6,boxShadow:"0 4px 16px rgba(0,0,0,0.14)",
          maxHeight:220,overflowY:"auto"}}>
          {filtered.length === 0 && (
            <div style={{padding:"8px 10px",fontSize:12,color:"var(--text-faint)",fontStyle:"italic"}}>
              {searching ? "Recherche…" : "Aucune station trouvée."}
            </div>
          )}
          {filtered.map(c=>(
            <div key={c.id} onClick={()=>{onChange(c.id); setOpen(false);}}
              style={{padding:"6px 10px",fontSize:12,cursor:"pointer",
                background:c.id===value?"#f2f7f2":"#fff"}}
              onMouseEnter={e=>{ (e.currentTarget as HTMLDivElement).style.background = c.id===value ? "#e9f3e9" : "#f5f5f5"; }}
              onMouseLeave={e=>{ (e.currentTarget as HTMLDivElement).style.background = c.id===value ? "#f2f7f2" : "#fff"; }}>
              <StationLabel station={c}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const YEAR_SELECT_OPTIONS = Array.from({length:17}, (_, i) => new Date().getFullYear() - i);

export function WeatherPointModal({ site, lat, lon, onClose, onSaveSite }: {
  site: SiteRecord; lat: number; lon: number; onClose: () => void;
  onSaveSite: (s: SiteRecord) => Promise<void>;
}) {
  const persistedStationId = parseInt(site.data.coverProps?.meteoFranceStationId ?? "", 10);

  // Plage d'années — dernière année pleinement terminée par défaut (l'année en cours a un DJU/température incomplets).
  const [yearEnd,   setYearEndRaw]   = useState(new Date().getFullYear() - 1);
  const [yearStart, setYearStartRaw] = useState(new Date().getFullYear() - 5);
  const setYearEnd   = (y:number) => { setYearEndRaw(y);   setYearStartRaw(s => Math.min(s, y)); };
  const setYearStart = (y:number) => { setYearStartRaw(y); setYearEndRaw(e => Math.max(e, y)); };
  // Ordre décroissant (année la plus récente en premier) — pour l'affichage tableau/légende.
  const years = useMemo(() => Array.from({length: Math.max(0, yearEnd - yearStart + 1)}, (_, i) => yearEnd - i), [yearStart, yearEnd]);

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("dju");
  const [viewMode,  setViewMode]  = useState<ViewMode>("table");
  const [djuMode,   setDjuMode]   = useState<DjuMode>("chauffage");
  const [precise,   setPrecise]   = useState(false);
  const [yearSort,  setYearSort]  = useState<"asc"|"desc">("desc");

  // Recherche des stations proches (recommandation basée sur la dernière année de la plage),
  // annotées de leur complétude sur toute la période choisie (pas seulement `yearEnd`) —
  // l'ordre/l'ensemble des candidates est indépendant de l'année (tri par distance uniquement),
  // donc une requête par année de la plage suffit à fusionner leur complétude.
  const [stationCandidates, setStationCandidates] = useState<PeriodStationCandidate[]>([]);
  const [stationsLoading,   setStationsLoading]    = useState(false);
  const [stationsError,     setStationsError]      = useState<string|null>(null);
  const [selectedStationId, setSelectedStationId]  = useState<number | null>(Number.isFinite(persistedStationId) ? persistedStationId : null);
  const [onlyComplete,      setOnlyComplete]       = useState(true);

  useEffect(() => {
    let active = true;
    setStationsLoading(true); setStationsError(null);
    fetchNearestStations(lat, lon, yearEnd)
      .then(async ({ candidates, recommended }) => {
        if (!active) return;
        let merged: PeriodStationCandidate[] = candidates.map(c => (
          { ...c, completeForPeriod: c.completeForYear, missingDaysInPeriod: c.missingDaysInYear }
        ));
        const otherYears = years.filter(y => y !== yearEnd);
        if (otherYears.length > 0) {
          const perYear = await Promise.all(otherYears.map(y => fetchNearestStations(lat, lon, y).catch(() => null)));
          if (!active) return;
          for (const res of perYear) {
            if (!res) continue;
            const byId = new Map(res.candidates.map(c => [c.id, c]));
            merged = merged.map(c => {
              const other = byId.get(c.id);
              if (!other) return c;
              return {
                ...c,
                completeForPeriod: c.completeForPeriod && other.completeForYear,
                missingDaysInPeriod: c.missingDaysInPeriod + other.missingDaysInYear,
              };
            });
          }
        }
        if (!active) return;
        setStationCandidates(merged);
        setSelectedStationId(prev => prev ?? recommended ?? merged[0]?.id ?? null);
      })
      .catch(e => { if (active) setStationsError((e as Error).message); })
      .finally(() => { if (active) setStationsLoading(false); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, yearStart, yearEnd]);

  // Météo de la station sélectionnée, une année à la fois — fusionnées par année.
  const [weatherByYear, setWeatherByYear] = useState<Record<number, StationWeather>>({});
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError,   setWeatherError]   = useState<string|null>(null);

  useEffect(() => {
    if (selectedStationId == null || years.length === 0) return;
    let active = true;
    setWeatherLoading(true); setWeatherError(null);
    Promise.allSettled(years.map(y => fetchStationWeather(selectedStationId, y).then(d => [y, d] as const)))
      .then(results => {
        if (!active) return;
        const entries = results
          .filter((r): r is PromiseFulfilledResult<readonly [number, StationWeather]> => r.status === "fulfilled")
          .map(r => r.value);
        setWeatherByYear(Object.fromEntries(entries));
        if (entries.length === 0) setWeatherError("Impossible de récupérer les données météo pour cette plage d'années.");
      })
      .finally(() => { if (active) setWeatherLoading(false); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStationId, yearStart, yearEnd]);

  const hasChanges = selectedStationId !== (Number.isFinite(persistedStationId) ? persistedStationId : null);

  const [confirmNonNearest, setConfirmNonNearest] = useState(false);

  const doSaveChoice = async () => {
    if (selectedStationId == null) return;
    setSaving(true);
    try {
      const coverProps = { ...site.data.coverProps, meteoFranceStationId: String(selectedStationId) };
      await onSaveSite({ ...site, data: { ...site.data, coverProps } });
    } finally { setSaving(false); }
  };

  const handleSaveChoice = () => {
    if (selectedStationId == null) return;
    // `stationCandidates` est trié par distance croissante (cf. findNearestStations côté serveur) —
    // le premier candidat complet sur la période est donc la station complète la plus proche.
    const nearestComplete = stationCandidates.find(c => c.completeForPeriod) ?? null;
    if (nearestComplete && nearestComplete.id !== selectedStationId) {
      setConfirmNonNearest(true);
      return;
    }
    void doSaveChoice();
  };

  // La station sélectionnée n'est pas forcément parmi les plus proches (choix manuel antérieur) —
  // on l'ajoute aux options si nécessaire, à partir des métadonnées renvoyées avec sa météo.
  const anyWeather = Object.values(weatherByYear)[0];
  const stationOptions: PeriodStationCandidate[] = selectedStationId != null && !stationCandidates.some(c => c.id === selectedStationId) && anyWeather?.station
    ? [{ ...anyWeather.station, distanceKm: NaN, completeForYear: true, missingDaysInYear: 0, completeForPeriod: true, missingDaysInPeriod: 0, dateDebut:0, dateFin:0, count:0, missing:0 } as PeriodStationCandidate, ...stationCandidates]
    : stationCandidates;

  // Suggestions filtrées sur la complétude de la période choisie — la station déjà
  // sélectionnée reste visible même incomplète, pour ne pas faire disparaître le choix courant.
  const suggestedOptions = onlyComplete
    ? stationOptions.filter(c => c.completeForPeriod || c.id === selectedStationId)
    : stationOptions;

  // ── Agrégat annuel : Total (DJU, somme) ou Moyenne (température) ────────────
  const field = activeTab === "dju"
    ? (djuMode === "climatisation" ? "monthlyDjuFroid" as const : "monthlyDju" as const)
    : "monthlyTemp" as const;
  const unit  = activeTab === "dju" ? "" : "°C";
  const yearAggregate = (y: number): number | null => {
    const w = weatherByYear[y];
    if (!w) return null;
    const vals: number[] = [];
    for (let m = 1; m <= 12; m++) {
      const v = w[field][`${y}-${String(m).padStart(2,"0")}`];
      if (v != null) vals.push(v);
    }
    if (!vals.length) return null;
    const sum = vals.reduce((a,b)=>a+b,0);
    return activeTab === "dju" ? sum : sum / vals.length;
  };

  // ── Moyennes sur la période choisie (toutes années confondues) ──────────────
  const monthAverage = (m: number): number | null => {
    const mk = String(m).padStart(2,"0");
    const vals: number[] = [];
    for (const y of years) {
      const v = weatherByYear[y]?.[field]?.[`${y}-${mk}`];
      if (v != null) vals.push(v);
    }
    return vals.length ? vals.reduce((a,b)=>a+b,0) / vals.length : null;
  };
  const periodAverage = (() => {
    const vals = years.map(yearAggregate).filter((v): v is number => v != null);
    return vals.length ? vals.reduce((a,b)=>a+b,0) / vals.length : null;
  })();

  // ── Rendu Highcharts (mode courbe) ──────────────────────────────────────────
  const chartRef     = useRef<HTMLDivElement>(null);
  const chartInstRef = useRef<any>(null);
  const doRenderRef  = useRef<()=>void>(()=>{});
  doRenderRef.current = () => {
    if (!chartRef.current || !window.Highcharts) return;
    if (chartInstRef.current) { try { chartInstRef.current.destroy(); } catch { /* noop */ } chartInstRef.current = null; }
    const series = years.map(y => {
      const w = weatherByYear[y];
      const data = Array.from({length:12}, (_, m) => w?.[field]?.[`${y}-${String(m+1).padStart(2,"0")}`] ?? null);
      // Couleur basée sur l'ancienneté réelle (yearEnd - y), indépendante de l'ordre d'affichage
      // (années triées décroissant) — le vert est réservé à la courbe "Moyenne" ci-dessous ;
      // nuances de bleu, du plus foncé (année la plus récente) au plus clair (la plus ancienne).
      const age = yearEnd - y;
      const color = age === 0 ? "#1a4a8a" : lerpHex("#1a4a8a", "#bcd6f2", age / Math.max(years.length - 1, 1));
      return { type:"area" as const, name:String(y), color, data, fillOpacity:0.12, marker:{enabled:true,radius:2} };
    });
    // Moyenne sur la période choisie — ligne pointillée verte au-dessus des aires annuelles
    series.push({
      type:"line" as any, name:"Moyenne", color:"#00FF00", dashStyle:"ShortDash" as any,
      data: Array.from({length:12}, (_, m) => monthAverage(m+1)),
      lineWidth:2, marker:{enabled:true,radius:3,symbol:"diamond"}, zIndex:5,
    } as any);
    chartInstRef.current = window.Highcharts.chart(chartRef.current, {
      chart:{ backgroundColor:"transparent", spacing:[8,8,8,0], animation:false },
      title:{ text:null }, credits:{ enabled:false },
      legend:{ enabled:true, itemStyle:{fontSize:"10px",fontFamily:"var(--ff-title)"} },
      xAxis:{ categories: MOIS_LABELS, lineColor:"#ddd6c8", tickColor:"transparent",
        labels:{style:{fontSize:"10px",color:"#888"}} },
      yAxis:{ title:{text: activeTab==="dju" ? (djuMode==="climatisation" ? "DJU froid" : "DJU") : "°C", style:{fontSize:"10px",color:"#aaa"}},
        gridLineColor:"#ede8df", gridLineDashStyle:"Dot" as any,
        labels:{style:{fontSize:"10px",color:"#999"}} },
      tooltip:{ shared:true, valueDecimals: precise ? 1 : 0, valueSuffix: unit ? ` ${unit}` : "" },
      plotOptions:{ area:{ marker:{enabled:true} } },
      series,
    });
  };

  useEffect(() => {
    if (viewMode !== "chart") return;
    let active = true;
    loadHCAndRender(() => { if (active) doRenderRef.current(); });
    return () => {
      active = false;
      if (chartInstRef.current) { try { chartInstRef.current.destroy(); } catch { /* noop */ } chartInstRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, activeTab, djuMode, precise, weatherByYear, yearStart, yearEnd]);

  const fmt = (v: number) => precise ? v.toFixed(1) : String(Math.round(v));
  const sortedYears = yearSort === "desc" ? years : [...years].reverse();

  const selectedStationForConfirm = stationOptions.find(c => c.id === selectedStationId) ?? null;
  const nearestCompleteForConfirm = stationCandidates.find(c => c.completeForPeriod) ?? null;

  return (
    <>
    <Modal title={`Données Météo — ${site.name}`} onClose={onClose} maxWidth="min(1400px, 96vw)">
      <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:14,overflowY:"auto"}}>

        {/* Station + plage d'années */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
          <div style={{display:"flex",alignItems:"center",gap:6,flex:1,minWidth:220}}>
            <span style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"var(--text-muted)",flexShrink:0}}>
              <img src="/meteo_france_logo.png" alt="" style={{width:14,height:14,borderRadius:3,objectFit:"cover"}}/>
              Station
            </span>
            <StationAutocomplete options={suggestedOptions} value={selectedStationId} onChange={setSelectedStationId} loading={stationsLoading}
              globalSearch={onlyComplete ? undefined : { lat, lon, year: yearEnd }}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:11,color:"var(--text-muted)"}}>De</span>
            <select value={yearStart} onChange={e=>setYearStart(Number(e.target.value))}
              className="field" style={{marginBottom:0,fontSize:13,width:90,padding:"4px 8px"}}>
              {YEAR_SELECT_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span style={{fontSize:11,color:"var(--text-muted)"}}>à</span>
            <select value={yearEnd} onChange={e=>setYearEnd(Number(e.target.value))}
              className="field" style={{marginBottom:0,fontSize:13,width:90,padding:"4px 8px"}}>
              {YEAR_SELECT_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap",marginTop:-8}}>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:"var(--text-muted)",cursor:"pointer"}}>
            <input type="checkbox" checked={onlyComplete} onChange={e=>setOnlyComplete(e.target.checked)}/>
            Ne suggérer que les stations complètes sur la période choisie
          </label>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:"var(--text-muted)",cursor:"pointer"}}>
            <input type="checkbox" checked={precise} onChange={e=>setPrecise(e.target.checked)}/>
            Afficher les valeurs au dixième (0,1)
          </label>
        </div>

        {/* Onglets + bascule tableau/courbe */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid #eee",flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{display:"flex",gap:2}}>
              {(["dju","temp"] as Tab[]).map(id => {
                const active = activeTab === id;
                const showToggle = id === "dju" && active;
                return (
                  <div key={id} onClick={()=>setActiveTab(id)} role="button" tabIndex={0}
                    onKeyDown={e=>{ if (e.key==="Enter"||e.key===" ") { e.preventDefault(); setActiveTab(id); } }}
                    style={{height:30,boxSizing:"border-box",padding:"0 14px",display:"flex",alignItems:"center",gap:8,
                      cursor:"pointer",fontSize:12,fontWeight:active?700:400,
                      color:active?"var(--text-primary)":"var(--text-muted)",
                      borderBottom:active?"2px solid var(--green-dim,#5a9)":"2px solid transparent"}}>
                    {id==="dju" ? "DJU" : "Températures 🌡"}
                    {showToggle && <DjuModeToggle value={djuMode} onChange={setDjuMode}/>}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{display:"flex",gap:2,paddingBottom:4}}>
            {([["table","▦ Tableau"],["chart","📈 Courbe"]] as [ViewMode,string][]).map(([id,label]) => (
              <button key={id} onClick={()=>setViewMode(id)} className="btn btn-sm"
                style={{fontSize:11,padding:"3px 10px",
                  background:viewMode===id?"var(--btn-dark,#1a1a2e)":"transparent",
                  color:viewMode===id?"#fff":"var(--text-muted)",
                  border:viewMode===id?"none":"1px solid #ddd"}}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {stationsError && <div style={{color:"var(--red)",fontSize:12}}>⚠ {stationsError}</div>}
        {!stationsLoading && stationOptions.length===0 && !stationsError && (
          <div style={{fontSize:12,color:"var(--text-faint)",fontStyle:"italic"}}>Aucune station Météo-France trouvée à proximité.</div>
        )}
        {!stationsLoading && stationOptions.length>0 && suggestedOptions.length===0 && (
          <div style={{fontSize:12,color:"var(--text-faint)",fontStyle:"italic"}}>Aucune station complète sur cette période — décochez le filtre pour voir toutes les stations.</div>
        )}
        {weatherLoading && <div style={{fontSize:12,color:"var(--text-faint)"}}>Chargement…</div>}
        {weatherError && <div style={{color:"var(--red)",fontSize:12}}>⚠ {weatherError}</div>}

        {!weatherLoading && !weatherError && years.length > 0 && (
          viewMode === "chart" ? (
            <div ref={chartRef} style={{height:340,width:"100%"}}/>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{borderCollapse:"collapse",fontSize:12,fontFamily:"var(--ff-title)",width:"100%"}}>
                <thead>
                  <tr style={{background:"#efefef"}}>
                    <th onClick={()=>setYearSort(s => s==="desc" ? "asc" : "desc")} title="Trier par année"
                      style={{padding:"6px 10px",textAlign:"left",borderBottom:"1px solid #e0e0e0",fontWeight:700,cursor:"pointer",userSelect:"none"}}>
                      Année {yearSort==="desc" ? "▼" : "▲"}
                    </th>
                    {MOIS_LABELS.map(label => (
                      <th key={label} title={activeTab==="dju" ? djuTooltip(djuMode) : undefined}
                        style={{padding:"6px 8px",textAlign:"right",borderBottom:"1px solid #e0e0e0",fontWeight:700,cursor:activeTab==="dju"?"help":undefined}}>
                        {label}
                      </th>
                    ))}
                    <th style={{padding:"6px 10px",textAlign:"right",borderBottom:"1px solid #e0e0e0",fontWeight:700}}>
                      {activeTab==="dju" ? "Total" : "Moyenne"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedYears.map((y, i) => {
                    const w = weatherByYear[y];
                    const agg = yearAggregate(y);
                    return (
                      <tr key={y} style={{background:i%2===0?"#fff":"#f6f6f6"}}>
                        <td style={{padding:"5px 10px",borderBottom:"1px solid #f0f0f0",color:"var(--text-muted)",whiteSpace:"nowrap"}}>
                          {y}
                          {w && !w.complete && (
                            <span title={`${w.missingDays} jour${w.missingDays>1?"s":""} manquant${w.missingDays>1?"s":""}`} style={{color:"#e8a838"}}> ⚠</span>
                          )}
                        </td>
                        {Array.from({length:12}, (_, m) => {
                          const v = w?.[field]?.[`${y}-${String(m+1).padStart(2,"0")}`];
                          return (
                            <td key={m} style={{padding:"5px 8px",textAlign:"right",borderBottom:"1px solid #f0f0f0",color:"var(--text-primary)"}}>
                              {v != null ? fmt(v) : "—"}
                            </td>
                          );
                        })}
                        <td style={{padding:"5px 10px",textAlign:"right",borderBottom:"1px solid #f0f0f0",color:"var(--text-primary)",fontWeight:600,whiteSpace:"nowrap"}}>
                          {agg != null ? fmt(agg) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:"#e9e9e9",fontWeight:700}}>
                    <td style={{padding:"6px 10px",borderTop:"2px solid #ccc",color:"var(--text-primary)",whiteSpace:"nowrap"}}>Moyenne</td>
                    {Array.from({length:12}, (_, m) => {
                      const v = monthAverage(m+1);
                      return (
                        <td key={m} style={{padding:"6px 8px",textAlign:"right",borderTop:"2px solid #ccc",color:"var(--text-primary)"}}>
                          {v != null ? fmt(v) : "—"}
                        </td>
                      );
                    })}
                    <td style={{padding:"6px 10px",textAlign:"right",borderTop:"2px solid #ccc",color:"var(--text-primary)",whiteSpace:"nowrap"}}>
                      {periodAverage != null ? fmt(periodAverage) : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )
        )}

        {/* Persistance du choix de station pour ce site */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingTop:6,borderTop:"1px solid #f0f0f0"}}>
          <span style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:"var(--text-muted)",alignSelf:"flex-start"}}>
            {selectedStationForConfirm && Number.isFinite(selectedStationForConfirm.lat) && Number.isFinite(selectedStationForConfirm.lon) && (
              <>
                <span title="Coordonnées de la station">
                  📍 {selectedStationForConfirm.lat.toFixed(4)}, {selectedStationForConfirm.lon.toFixed(4)}
                </span>
                <a href={`https://www.infoclimat.fr/stations-meteo/?s=${selectedStationForConfirm.id}`}
                  target="_blank" rel="noopener noreferrer"
                  title="Fiche de la station sur Infoclimat.fr (peut être indisponible pour les stations climatologiques sans télémesure temps réel)"
                  style={{color:"var(--green-dim,#5a9)",textDecoration:"none"}}>
                  ℹ️ Fiche station
                </a>
              </>
            )}
          </span>
          <button onClick={handleSaveChoice} disabled={!hasChanges || saving || selectedStationId==null} className="btn btn-black btn-sm">
            {saving ? "Enregistrement…" : "💾 Utiliser cette station pour ce site"}
          </button>
        </div>

      </div>
    </Modal>

    {confirmNonNearest && (
      <ConfirmModal
        title="Station non recommandée"
        message={
          `« ${selectedStationForConfirm?.nom ?? "cette station"} » n'est pas la station la plus proche ayant des données complètes sur la période choisie`
          + (nearestCompleteForConfirm ? ` (« ${nearestCompleteForConfirm.nom} » l'est).` : ".")
          + " Utiliser quand même cette station ?"
        }
        confirmLabel="Utiliser quand même"
        onConfirm={() => { void doSaveChoice(); }}
        onCancel={() => setConfirmNonNearest(false)}
      />
    )}
    </>
  );
}
